<template>
  <div class="modal-backdrop">
    <div class="modal-container">
      <div class="modal-header">
        <h3 class="modal-title">{{ isEditing ? 'Edit Course URL' : 'Create Course URL' }}</h3>
        <button class="close-btn" @click="$emit('close')">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="$emit('submit')">
          <div class="form-group">
            <label for="course_id">Course ID*</label>
            <div class="course-select-container">
              <input 
                type="number" 
                id="course_id" 
                :value="form.course_id" 
                @input="updateFormField('course_id', $event.target.value ? Number($event.target.value) : null)"
                required
                :disabled="formSubmitting"
                class="form-control"
              >
              <button 
                type="button" 
                class="btn btn-secondary" 
                @click="$emit('load-courses')" 
                :disabled="loadingCourses"
              >
                {{ loadingCourses ? 'Loading...' : 'Browse Courses' }}
              </button>
            </div>
            <p v-if="validationErrors.course_id" class="error-text">{{ validationErrors.course_id }}</p>
          </div>

          <!-- Image Uploader (English) -->
          <div class="form-group">
            <label>Image (English)*</label>
            <ImageUploader 
              :value="form.image_url"
              @input="updateFormField('image_url', $event)"
              placeholder="Upload English version image"
              :disabled="formSubmitting"
              @upload-success="$emit('upload-success', 'image_url')"
              @upload-error="$emit('upload-error', $event)"
            />
            <p v-if="validationErrors.image_url" class="error-text">{{ validationErrors.image_url }}</p>
          </div>

          <!-- Image Uploader (Arabic) -->
          <div class="form-group">
            <label>Image (Arabic)*</label>
            <ImageUploader 
              :value="form.image_url_ar"
              @input="updateFormField('image_url_ar', $event)"
              placeholder="Upload Arabic version image"
              :disabled="formSubmitting"
              @upload-success="$emit('upload-success', 'image_url_ar')"
              @upload-error="$emit('upload-error', $event)"
            />
            <p v-if="validationErrors.image_url_ar" class="error-text">{{ validationErrors.image_url_ar }}</p>
          </div>

          <!-- Video Upload Form -->
          <div class="form-group">
            <label>Preview Video</label>
            <div class="video-upload-container">
              <button 
                type="button" 
                class="video-upload-btn" 
                @click="$emit('open-video-uploader')" 
                :disabled="formSubmitting"
              >
                <i class="fas fa-video"></i> Upload Video
              </button>
              <div v-if="form.preview_thumbnail_url" class="video-preview">
                <img :src="form.preview_thumbnail_url" alt="Video thumbnail" class="thumbnail" />
                <div class="video-info">
                  <span>Video uploaded successfully</span>
                  <button type="button" class="remove-btn" @click="$emit('remove-video')">Remove</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Downloadable URL -->
          <div class="form-group">
            <label for="downloadable_url">Downloadable URL</label>
            <input 
              type="text" 
              id="downloadable_url" 
              :value="form.downloadable_url" 
              @input="updateFormField('downloadable_url', $event.target.value)"
              :disabled="formSubmitting"
              class="form-control"
              placeholder="Enter URL for downloadable content"
            >
            <p v-if="validationErrors.downloadable_url" class="error-text">{{ validationErrors.downloadable_url }}</p>
          </div>

          <div class="form-actions">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="$emit('close')" 
              :disabled="formSubmitting"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              class="btn btn-primary" 
              :disabled="formSubmitting"
            >
              <span v-if="formSubmitting">
                <i class="fas fa-spinner fa-spin"></i> Saving...
              </span>
              <span v-else>
                {{ isEditing ? 'Update' : 'Create' }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import ImageUploader from '../ImageUploader.vue';

export default {
  name: 'CourseFormModal',
  components: {
    ImageUploader
  },
  props: {
    isEditing: {
      type: Boolean,
      default: false
    },
    form: {
      type: Object,
      required: true
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    formSubmitting: {
      type: Boolean,
      default: false
    },
    loadingCourses: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'close', 
    'submit', 
    'load-courses', 
    'select-course', 
    'upload-success', 
    'upload-error',
    'open-video-uploader',
    'remove-video',
    'update:form'
  ],
  methods: {
    updateFormField(field, value) {
      const updatedForm = { ...this.form, [field]: value };
      this.$emit('update:form', updatedForm);
    }
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

.modal-container {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 35rem;
  max-height: 90vh;
  animation: slideIn 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #343a40;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.close-btn:hover {
  opacity: 1;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #343a40;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.form-control:focus {
  border-color: #3498db;
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
}

.course-select-container {
  display: flex;
  gap: 0.75rem;
}

.course-select-container .form-control {
  flex: 1;
}

.error-text {
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.375rem;
}

.video-upload-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.video-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-self: flex-start;
}

.video-upload-btn:hover:not(:disabled) {
  background-color: #2980b9;
}

.video-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.thumbnail {
  width: 4rem;
  height: 4rem;
  object-fit: cover;
  border-radius: 0.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.video-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.remove-btn {
  align-self: flex-start;
  background-color: #fff;
  color: #dc3545;
  border: 1px solid #dc3545;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background-color: #dc3545;
  color: white;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  padding: 0.75rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2980b9;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #343a40;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e9ecef;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .course-select-container {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .video-preview {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .form-actions {
    flex-direction: column-reverse;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style> 